/**
 * Admin Review Moderation Queue
 *
 * Dashboard for moderating user reviews — approve, reject, or flag.
 * Backend serves pending + flagged reviews via GET /api/v1/reviews/pending.
 *
 * API Endpoints (all exist — no backend changes):
 *   GET  /api/v1/reviews/pending?limit=20&offset=0
 *   POST /api/v1/reviews/:reviewId/approve
 *   POST /api/v1/reviews/:reviewId/reject   { reason }
 *   POST /api/v1/reviews/:reviewId/flag      { reason }
 *
 * Story: SCRUM-840
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ReviewCard from '../../components/admin/ReviewCard';
import RejectReasonModal from '../../components/admin/RejectReasonModal';
import Pagination from '../../components/admin/Pagination';
import useReviewModeration from '../../hooks/admin/useReviewModeration';
import { RefreshCw } from 'lucide-react';

const ITEMS_PER_PAGE = 20;

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'flagged', label: 'Flagged' },
];

const SORT_OPTIONS = [
  { value: 'date', label: 'Date Submitted' },
  { value: 'rating', label: 'Rating' },
];

export default function ReviewModerationQueue() {
  const navigate = useNavigate();

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const offset = (currentPage - 1) * ITEMS_PER_PAGE;

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('date');

  // Expand state
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Modal state
  const [rejectTarget, setRejectTarget] = useState(null);
  const [flagTarget, setFlagTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const {
    reviews,
    loading,
    error,
    actionLoading,
    refresh,
    approveReview,
    rejectReview,
    flagReview,
  } = useReviewModeration({ limit: ITEMS_PER_PAGE, offset });

  // Client-side filter + sort (API returns pending + flagged)
  const filteredReviews = reviews
    .filter(r => !statusFilter || r.moderationStatus === statusFilter)
    .sort((a, b) => {
      if (sortBy === 'rating') {
        return (b.overallRating || 0) - (a.overallRating || 0);
      }
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });

  const totalPages = Math.max(1, Math.ceil(filteredReviews.length / ITEMS_PER_PAGE));

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const toggleExpand = useCallback((reviewId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(reviewId)) {
        next.delete(reviewId);
      } else {
        next.add(reviewId);
      }
      return next;
    });
  }, []);

  const handleApprove = useCallback(async (reviewId) => {
    try {
      await approveReview(reviewId);
      showToast('Review approved and published');
    } catch (err) {
      showToast(err.message || 'Failed to approve review', 'error');
    }
  }, [approveReview, showToast]);

  const handleReject = useCallback(async (reason) => {
    if (!rejectTarget) { return; }
    await rejectReview(rejectTarget, reason);
    showToast('Review rejected');
    setRejectTarget(null);
  }, [rejectTarget, rejectReview, showToast]);

  const handleFlag = useCallback(async (reason) => {
    if (!flagTarget) { return; }
    await flagReview(flagTarget, reason);
    showToast('Review flagged for moderation');
    setFlagTarget(null);
  }, [flagTarget, flagReview, showToast]);

  const pendingCount = reviews.filter(r => r.moderationStatus === 'pending').length;
  const flaggedCount = reviews.filter(r => r.moderationStatus === 'flagged').length;

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="review-moderation"
      onNavigate={(id, path) => path && navigate(path)}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Review Moderation</h1>
            <p className="text-sm text-gray-500 mt-1">
              {pendingCount} pending, {flaggedCount} flagged
            </p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 transition text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div
            className={`p-3 rounded-lg text-sm font-medium ${
              toast.type === 'error'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}
            role="alert"
          >
            {toast.message}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            aria-label="Filter by status"
          >
            {STATUS_FILTERS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            aria-label="Sort reviews"
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-12 text-gray-500">Loading reviews...</div>
        )}

        {/* Empty */}
        {!loading && filteredReviews.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No reviews to moderate</p>
            <p className="text-gray-400 text-sm mt-1">All caught up!</p>
          </div>
        )}

        {/* Review cards */}
        {!loading && filteredReviews.length > 0 && (
          <div className="space-y-4">
            {filteredReviews.map(review => (
              <ReviewCard
                key={review.reviewId}
                review={review}
                expanded={expandedIds.has(review.reviewId)}
                onToggle={() => toggleExpand(review.reviewId)}
                onApprove={handleApprove}
                onReject={(id) => setRejectTarget(id)}
                onFlag={(id) => setFlagTarget(id)}
                disabled={actionLoading}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Reject modal */}
      <RejectReasonModal
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleReject}
        title="Reject Review"
        actionLabel="Reject"
        loading={actionLoading}
      />

      {/* Flag modal */}
      <RejectReasonModal
        isOpen={!!flagTarget}
        onClose={() => setFlagTarget(null)}
        onConfirm={handleFlag}
        title="Flag Review"
        actionLabel="Flag"
        loading={actionLoading}
      />
    </DashboardLayout>
  );
}
